// Brief 70.1 (24 Jun 2026): read-only Matter Activity feed. Surfaces the
// merged ccl_audit_events + forf_audit_events (+ matter_audit /
// matter_activity_log) trail from GET /api/matter/{ref}/activity as a
// vertical timeline so Vanessa / Rebekah can see who did what when.
//
// READ-ONLY: nothing here mutates state. The only actions are a refresh
// button and per-row expand-for-details disclosure.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import { formatDistanceToNow, format, parseISO } from "date-fns";
import {
  Activity,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  Loader2,
  Cog,
} from "lucide-react";

// Category → dot colour. CCL=teal, FORF=purple, signing=gold, other=gray.
const CATEGORY_DOT = {
  ccl: "bg-teal",
  forf: "bg-purple-400",
  signing: "bg-amber-400",
  other: "bg-slate-500",
};

const CATEGORY_LABEL = {
  ccl: "CCL",
  forf: "FORF",
  signing: "Signing",
  other: "Activity",
};

function relativeTime(ts) {
  if (!ts) return "";
  try {
    return formatDistanceToNow(parseISO(ts), { addSuffix: true });
  } catch {
    return "";
  }
}

function absoluteTime(ts) {
  if (!ts) return "";
  try {
    return format(parseISO(ts), "dd MMM yyyy, HH:mm");
  } catch {
    return ts;
  }
}

function initials(event) {
  // BM-H4491: the backend resolves staff to their own initials (VEC, HD).
  if (event.actor_initials) return event.actor_initials;
  const source = event.actor_display_name || event.actor_email || "";
  if (!source) return "—";
  const local = source.includes("@") ? source.split("@")[0] : source;
  const parts = local.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function TimelineRow({ event }) {
  const [open, setOpen] = useState(false);
  const cat = CATEGORY_DOT[event.category] ? event.category : "other";
  const hasDetails = event.details && Object.keys(event.details).length > 0;

  return (
    <li className="flex gap-3" data-testid={`activity-row-${event.id}`}>
      {/* Left: relative timestamp */}
      <div
        className="w-24 shrink-0 pt-0.5 text-right text-[11px] leading-tight text-txt-faint"
        title={absoluteTime(event.timestamp)}
      >
        {relativeTime(event.timestamp) || "—"}
      </div>

      {/* Center: category-coded dot + connecting spine */}
      <div className="relative flex shrink-0 flex-col items-center">
        <span
          aria-hidden="true"
          data-testid={`activity-dot-${cat}`}
          className={`mt-1 h-2.5 w-2.5 rounded-full ${CATEGORY_DOT[cat]}`}
        />
        <span aria-hidden="true" className="mt-1 w-px flex-1 bg-navy-border" />
      </div>

      {/* Right: summary + actor + expandable details */}
      <div className="min-w-0 flex-1 pb-4">
        <div className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy-card text-[10px] font-semibold text-txt-muted"
            title={event.actor_display_name || event.actor_email || "User not recorded"}
            data-testid={`activity-actor-${event.id}`}
          >
            {event.actor_is_system ? <Cog size={12} /> : initials(event)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] leading-snug text-txt">{event.summary}</p>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-[10px] font-medium uppercase tracking-wider text-txt-faint">
                {CATEGORY_LABEL[cat]}
              </span>
              {hasDetails && (
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  aria-expanded={open}
                  data-testid={`activity-expand-${event.id}`}
                  className="inline-flex items-center gap-0.5 text-[10px] font-medium text-txt-muted transition-colors hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/60 rounded"
                >
                  {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  Details
                </button>
              )}
            </div>
            {open && hasDetails && (
              <pre
                data-testid={`activity-details-${event.id}`}
                className="mt-1.5 overflow-x-auto rounded-lg border border-navy-border bg-navy px-2.5 py-2 text-[11px] leading-[1.5] text-txt-muted whitespace-pre-wrap"
              >
                {JSON.stringify(event.details, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function SkeletonRows() {
  return (
    <ul className="space-y-4" data-testid="activity-skeleton" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex gap-3">
          <div className="h-3 w-24 shrink-0 rounded bg-navy-card" />
          <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-navy-card" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded bg-navy-card" />
            <div className="h-2 w-1/4 rounded bg-navy-card" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function MatterActivityFeed({ matterReference, defaultCollapsed = true }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const fetchActivity = useCallback(async () => {
    if (!matterReference) return;
    setLoading(true);
    setError(false);
    try {
      const resp = await axios.get(
        `${API}/matter/${encodeURIComponent(matterReference)}/activity`,
      );
      setEvents(Array.isArray(resp.data?.events) ? resp.data.events : []);
      setLoaded(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [matterReference]);

  // Lazy-load: only fetch once the section is first expanded.
  useEffect(() => {
    if (!collapsed && !loaded && !loading) fetchActivity();
  }, [collapsed, loaded, loading, fetchActivity]);

  const count = events.length;

  return (
    <section
      data-testid="matter-activity-feed"
      className="mx-auto my-4 w-full max-w-3xl rounded-xl border border-navy-border bg-navy text-txt"
    >
      {/* Header — toggles the collapsible section, count badge alongside */}
      <div className="flex items-center gap-2 border-b border-navy-border px-4 py-3">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          data-testid="activity-toggle"
          className="flex flex-1 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/60 rounded"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-dim text-teal">
            <Activity size={14} />
          </span>
          <span className="text-[13px] font-semibold text-txt">Activity</span>
          {loaded && (
            <span
              data-testid="activity-count-badge"
              className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-navy-card px-1.5 text-[10px] font-semibold text-txt-muted"
            >
              {count}
            </span>
          )}
          <span className="ml-auto text-txt-muted">
            {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>
        {!collapsed && (
          <button
            type="button"
            onClick={fetchActivity}
            disabled={loading}
            data-testid="activity-refresh"
            title="Refresh activity"
            aria-label="Refresh activity"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-txt-muted transition-colors hover:bg-navy-hover hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/60 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        )}
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="px-4 py-4">
          {loading && !loaded ? (
            <SkeletonRows />
          ) : error ? (
            <div
              className="flex flex-col items-center gap-2 py-6 text-center"
              data-testid="activity-error"
            >
              <AlertTriangle size={18} className="text-status-amber" />
              <p className="text-[12.5px] text-txt-muted">
                Couldn’t load activity for this matter.
              </p>
              <button
                type="button"
                onClick={fetchActivity}
                disabled={loading}
                data-testid="activity-retry"
                className="inline-flex items-center gap-1.5 rounded-lg border border-navy-border px-3 py-1.5 text-[12px] font-medium text-txt-muted transition-colors hover:bg-navy-hover hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/60"
              >
                {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Retry
              </button>
            </div>
          ) : count === 0 ? (
            <p
              className="py-6 text-center text-[12.5px] text-txt-muted"
              data-testid="activity-empty"
            >
              No activity recorded yet
            </p>
          ) : (
            <ul data-testid="activity-list">
              {events.map((event) => (
                <TimelineRow key={event.id} event={event} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
